import { Box, Typography, Tooltip, IconButton, Chip } from '@mui/material';
import { Visibility, OpenInNew } from '@mui/icons-material';
import makeStyles from '@mui/styles/makeStyles';
import { useSelector } from 'react-redux';
import { insuranceSelector } from '../model';
import { Proposal } from '@trnsact/trnsact-shared-types';
import GAIG_LOGO from '../../../../assets/img/aftermarket-vendor-logos/gaig-logo.png';

const config = require('../../../../config');

const formatDate = (dateString: string | undefined | null) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const InsuranceProposals = () => {
  const classes = useStyles();
  const insuranceExpressProposals = useSelector(insuranceSelector.proposals);

  const getProposalLink = (proposal: Proposal) => {
    window.open(
      `https://app${config.environmentName === 'dev' ? '.dev' : ''}.trnsact.com/ps/?vendorGUID=${
        proposal.dynamicsAccountId
      }&proposalId=${proposal.id}&viewOnly=true`,
      '_blank'
    );
  };

  const handleGoToProposal = (proposal: Proposal) => {
    window.open(`/proposalDetail/${proposal.id}?from=/proposals`, '_blank');
  };

  return (
    <Box className={classes.root}>
      <Typography className={classes.proposalHeader}>Insurance Proposals:</Typography>

      {insuranceExpressProposals.map((proposal: Proposal, index: number) => (
        <Box key={`proposal-container-${index}`} className={classes.proposalBlock}>
          <Box className={classes.proposalItem}>
            <Typography className={classes.date}>{formatDate(proposal.createdDateTime)}</Typography>

            <img src={GAIG_LOGO} alt="GAIG Logo" width={128} height={64} />

            <Chip
              label={proposal.status ?? 'Sent to Customer'}
              sx={{
                color: '#fff',
                bgcolor: proposal.status === 'Accepted' ? '#007F46' : '#1976d2',
                ml: 1,
              }}
              size="small"
            />

            <Tooltip title="Go to internal proposal details">
              <IconButton size="small" color="primary" onClick={() => handleGoToProposal(proposal)}>
                <Visibility />
              </IconButton>
            </Tooltip>

            <Tooltip title="Go to proposal and see what the applicant sees">
              <IconButton size="small" color="primary" onClick={() => getProposalLink(proposal)}>
                <OpenInNew />
              </IconButton>
            </Tooltip>
          </Box>

          {proposal.status === 'Accepted' && (
            <Typography className={classes.proposalNote} variant="body2" color="textSecondary">
              View the proposal and click "Begin Contracting" to get a Certificate of Insurance.
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );
};

const useStyles = makeStyles({
  root: {
    width: '100%', // or try "600px" for fixed, or "80%" for relative
    maxWidth: '800px', // optional if you want a cap
  },
  proposalBlock: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 8,
    width: '100%', // ensure it stretches full width of parent
  },
  proposalNote: {
    marginTop: 4,
    width: '100%', // stretches to parent container
  },
  proposalHeader: {
    color: '#000000de',
    fontWeight: 500,
    marginBottom: 4,
    display: 'flex',
    alignItems: 'center',
  },
  proposalItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '4px 0',
    width: '100%', // ensures it fills the parent container
    flexWrap: 'wrap', // allows wrapping if needed
  },
  date: {
    minWidth: 180,
    fontSize: '0.9rem',
  },
});
